/**
 * Aurélia Mail — SSO suite-shared (pattern Coffre / Agenda — docs/sso-apps-iframe-hub.md).
 */
import {
  MAIL_APP_DISPLAY_NAME,
  MAIL_AUTH_LOST_EVENT,
  MAIL_AUTH_READY_EVENT,
  MAIL_SUITE_APP_TILE,
  SUITE_FORBIDDEN_ERROR_CODES,
  SUITE_SESSION_ERROR_CODES,
} from "./constants";
import { getHub2WhoamiUrl, getMailPingUrl, getSsoVerifyUrl } from "./paths";
import {
  isMailEmbeddedInHub,
  isMailHub2Desk,
  isMailSessionVerified,
  readStoredSuiteUserId,
  mailAuthHeaders,
  markMailSessionVerified,
  persistSuiteUserId,
  syncSuiteUserIdFromQuery,
  clearMailSessionVerified,
} from "./session";
import { installMailAuthPopup } from "./mailAuthPopup";

type PingResult = {
  ok: boolean;
  forbidden?: boolean;
  sessionLost?: boolean;
  userId?: number;
  error?: string;
};

let verifyInFlight: Promise<boolean> | null = null;
let sessionLostCooldownUntil = 0;

function isAuthModalVisible(): boolean {
  const modal = document.getElementById("authModal");
  return Boolean(modal?.classList.contains("flex"));
}

/** Doc § cross-origin : pas de poll whoami (Planning) sur mail.app — iframe : postMessage hub suffit. */
function startMailHubSessionWatchIfNeeded(): void {
  return;
}

function hideRoot() {
  document.getElementById("root")?.classList.add("hidden");
}

function showRoot() {
  document.getElementById("root")?.classList.remove("hidden");
}

/** Évite que suite-auth-popup ferme la modale alors que #root est encore masqué (page blanche). */
export function isMailConnectedForPopup(): boolean {
  if (!isMailSessionVerified()) return false;
  const root = document.getElementById("root");
  return root != null && !root.classList.contains("hidden");
}

function patchHideSuiteAuthModal() {
  const native = window.hideSuiteAuthModal?.bind(window);
  if (!native || window.__mailHideAuthModalPatched) return;
  window.__mailHideAuthModalPatched = true;
  window.hideSuiteAuthModal = () => {
    native();
    showRoot();
  };
}

function lockShell() {
  window.lockSuiteAppShell?.();
  hideRoot();
}

function unlockShell() {
  window.unlockSuiteAppShell?.();
  showRoot();
}

function dispatchReady(userId: number) {
  window.dispatchEvent(
    new CustomEvent(MAIL_AUTH_READY_EVENT, { detail: { userId } })
  );
}

function dispatchLost() {
  window.dispatchEvent(new CustomEvent(MAIL_AUTH_LOST_EVENT));
}

function isForbiddenVisible(): boolean {
  const modal = document.getElementById("suiteForbiddenModal");
  return Boolean(modal?.classList.contains("flex"));
}

function mountSharedModals() {
  window.suiteAuthModalMount?.({ appName: MAIL_APP_DISPLAY_NAME });
  window.suiteForbiddenModalMount?.({ appName: MAIL_SUITE_APP_TILE });
  window.installSuiteAppShell?.({ shellId: "mailAppShell" });
  /* Pas installSuiteClearCacheListener : reload hub = tempête boot (mailSwBootstrap purge seule). */
}

function patchShowSuiteAuthModal() {
  const native = window.showSuiteAuthModal?.bind(window);
  if (!native || window.__mailShowAuthModalPatched) return;
  window.__mailShowAuthModalPatched = true;
  window.showSuiteAuthModal = (message?, options?) => {
    native(message, { ...options, autoReconnect: false });
  };
}

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

const AUTH_FETCH_TIMEOUT_MS = 12_000;

async function fetchWithTimeout(url: string, init: RequestInit = {}): Promise<Response> {
  const ctrl = new AbortController();
  const t = window.setTimeout(() => ctrl.abort(), AUTH_FETCH_TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: ctrl.signal });
  } finally {
    window.clearTimeout(t);
  }
}

function bindMailReconnect(): void {
  const btn = document.getElementById("btnOpenAuthPopup");
  if (!btn || btn.dataset.aureliaReconnect === "1") return;
  const fresh = btn.cloneNode(true) as HTMLButtonElement;
  fresh.dataset.aureliaReconnect = "1";
  btn.replaceWith(fresh);
  fresh.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (window.parent !== window && window.getSuiteAuthPopupUrl) {
      const popup = window.open(
        window.getSuiteAuthPopupUrl("mail"),
        "Aurélia Suite - Connexion",
        "width=520,height=680,scrollbars=yes,resizable=yes",
      );
      if (popup) return;
    }
    window.openSuiteAuthPopup?.("mail");
  });
}

function showHub2DeskMissing() {
  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
}

function showNeedLogin(_sessionLost = false) {
  if (isMailHub2Desk()) {
    showHub2DeskMissing();
    return;
  }
  if (isAuthModalVisible()) return;
  if (Date.now() < sessionLostCooldownUntil) return;

  lockShell();
  window.hideSuiteForbiddenModal?.();
  window.showSuiteAuthModal?.(undefined, { autoReconnect: false });
  dispatchLost();
}

function showForbidden() {
  if (isForbiddenVisible()) return;
  clearMailSessionVerified();
  window.hideSuiteAuthModal?.();
  lockShell();
  window.showSuiteForbiddenModal?.({
    appName: MAIL_SUITE_APP_TILE,
    actions: [
      {
        label: "Actualiser",
        primary: true,
        onClick: () => {
          window.hideSuiteForbiddenModal?.();
          window.location.reload();
        },
      },
    ],
  });
  dispatchLost();
}

function isForbiddenError(status: number, error: string): boolean {
  return (
    status === 403 &&
    (SUITE_FORBIDDEN_ERROR_CODES.has(error) || error === "admin_apps_disabled")
  );
}

function isSessionLostError(status: number, error: string): boolean {
  return status === 401 && SUITE_SESSION_ERROR_CODES.has(error);
}

async function fetchSsoVerify(userId: string | number): Promise<PingResult> {
  const url = getSsoVerifyUrl(userId, MAIL_SUITE_APP_TILE);
  try {
    const res = await fetchWithTimeout(url, { method: "GET", credentials: "include", cache: "no-store" });
    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      error?: string;
    };
    const err = typeof data.error === "string" ? data.error : "";
    if (!res.ok || !data.success) {
      const forbidden =
        res.status === 403 ||
        err === "app_not_permitted" ||
        err === "forbidden_app_permission" ||
        err === "admin_apps_disabled";
      return { ok: false, forbidden, error: err };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: "network_timeout" };
  }
}

async function fetchMailPing(): Promise<PingResult> {
  try {
    const res = await fetchWithTimeout(getMailPingUrl(), {
      credentials: "include",
      headers: { Accept: "application/json", ...mailAuthHeaders() },
    });
    const data = (await res.json().catch(() => ({}))) as {
      success?: boolean;
      userId?: number;
      error?: string;
    };
    const err = typeof data.error === "string" ? data.error : "";
    if (!res.ok || !data.success) {
      return {
        ok: false,
        forbidden: isForbiddenError(res.status, err),
        sessionLost: isSessionLostError(res.status, err),
        error: err,
      };
    }
    if (data.userId) persistSuiteUserId(data.userId);
    return { ok: true, userId: data.userId };
  } catch {
    return { ok: false, error: "network_timeout" };
  }
}

async function verifyAndEnter(
  userId?: string | number | null,
  options?: { silent?: boolean }
): Promise<boolean> {
  if (verifyInFlight) return verifyInFlight;

  verifyInFlight = (async () => {
    const resolvedId = userId ?? readStoredSuiteUserId();
    if (!resolvedId) return false;

    window.stopSuiteIframeSsoPoll?.();

    const sso = await fetchSsoVerify(resolvedId);
    if (!sso.ok) {
      if (sso.forbidden) {
        clearMailSessionVerified();
        if (!options?.silent) showForbidden();
        return false;
      }
      if (!options?.silent) {
        clearMailSessionVerified();
        showNeedLogin(true);
      }
      return false;
    }

    const ping = await fetchMailPing();
    if (!ping.ok) {
      if (ping.forbidden) {
        clearMailSessionVerified();
        if (!options?.silent) showForbidden();
        return false;
      }
      if (ping.sessionLost) {
        clearMailSessionVerified();
        if (!options?.silent) showNeedLogin(true);
        return false;
      }
      if (!options?.silent) {
        clearMailSessionVerified();
        showNeedLogin(true);
      }
      return false;
    }

    const uid = ping.userId ?? Number(resolvedId);
    persistSuiteUserId(uid);
    unlockShell();
    markMailSessionVerified();
    window.stopSuiteIframeSsoPoll?.();
    window.hideSuiteAuthModal?.();
    window.hideSuiteForbiddenModal?.();
    startMailHubSessionWatchIfNeeded();
    dispatchReady(uid);
    return true;
  })();

  try {
    return await verifyInFlight;
  } finally {
    verifyInFlight = null;
  }
}

async function verifyStoredUserInBackground(userId: string): Promise<void> {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const ok = await verifyAndEnter(userId, { silent: true });
    if (ok) return;
    await sleep(2_000 * (attempt + 1));
  }

  const ping = await fetchMailPing();
  if (!ping.ok && ping.sessionLost) {
    clearMailSessionVerified();
    showNeedLogin(true);
  }
}

async function enterFromHub2(): Promise<boolean> {
  let who: { ok?: boolean; user_id?: number } | null = null;
  try {
    const res = await fetchWithTimeout(getHub2WhoamiUrl(), {
      credentials: "include",
      cache: "no-store",
    });
    who = (await res.json().catch(() => null)) as { ok?: boolean; user_id?: number } | null;
    if (!res.ok || !who?.ok || !who.user_id) who = null;
  } catch {
    who = null;
  }
  if (!who?.user_id) {
    showHub2DeskMissing();
    return false;
  }
  const ping = await fetchMailPing();
  if (!ping.ok) {
    if (ping.forbidden) {
      showForbidden();
      return false;
    }
    showHub2DeskMissing();
    return false;
  }
  unlockShell();
  markMailSessionVerified();
  window.stopSuiteIframeSsoPoll?.();
  window.hideSuiteAuthModal?.();
  window.hideSuiteForbiddenModal?.();
  dispatchReady(ping.userId ?? who.user_id);
  return true;
}

export async function onMailSuiteAuthenticated(userIdArg: number): Promise<void> {
  if (isMailHub2Desk()) {
    await enterFromHub2();
    return;
  }
  const userId = userIdArg > 0 ? String(userIdArg) : readStoredSuiteUserId();
  if (!userId) return;
  await verifyAndEnter(userId);
}

export function handleMailSuiteSessionLost(): void {
  if (Date.now() < sessionLostCooldownUntil) return;
  sessionLostCooldownUntil = Date.now() + 8_000;

  clearMailSessionVerified();
  stopMailBackgroundSync();
  window.clearSuiteStoredIdentity?.();
  showNeedLogin(true);
}

function stopMailBackgroundSync(): void {
  window.stopSuiteIframeSsoPoll?.();
  window.stopSuiteHubSessionWatch?.();
}

export function mailNotifySuiteAuthError(status: number, error?: string): boolean {
  if (!isMailSessionVerified()) return false;
  const err = typeof error === "string" ? error : "";
  if (isForbiddenError(status, err)) {
    showForbidden();
    return true;
  }
  if (isSessionLostError(status, err)) {
    handleMailSuiteSessionLost();
    return true;
  }
  return false;
}

async function bootMailAuth(): Promise<void> {
  if (isMailHub2Desk()) {
    await enterFromHub2();
    return;
  }

  const delay = isMailEmbeddedInHub() ? 1200 : 400;
  await sleep(delay);

  const userId = readStoredSuiteUserId();
  if (userId) {
    unlockShell();
    markMailSessionVerified();
    dispatchReady(Number(userId));
    void verifyStoredUserInBackground(userId);
    return;
  }

  lockShell();
  showNeedLogin();
}

function runMailSharedInit(): void {
  mountSharedModals();
  patchHideSuiteAuthModal();
  patchShowSuiteAuthModal();

  installMailAuthPopup(handleMailSuiteSessionLost);
  bindMailReconnect();
  /* Pas de hub session watch (whoami/storage → boucles session lost sur mail.app cross-origin). */

  void bootMailAuth();
}

export function startMailSuiteAuth(): void {
  if (window.__mailSuiteAuthStarted) return;
  window.__mailSuiteAuthStarted = true;

  syncSuiteUserIdFromQuery();

  const run = () => {
    runMailSharedInit();
  };

  if (window.__SUITE_SHARED_READY__) {
    run();
    return;
  }

  document.addEventListener("suiteSharedReady", run, { once: true });
  document.addEventListener("suiteSharedFailed", () => {
    console.error("Email : échec chargement suite-shared");
    lockShell();
  });
}

export async function verifyMailSuiteAccess(): Promise<boolean> {
  if (isMailHub2Desk()) return enterFromHub2();
  syncSuiteUserIdFromQuery();
  return verifyAndEnter(readStoredSuiteUserId());
}

export function mailShowNeedLogin(sessionLost = false): void {
  showNeedLogin(sessionLost);
}

export function isSuiteSessionErrorCode(code: string | undefined): boolean {
  return !!code && SUITE_SESSION_ERROR_CODES.has(code);
}

export { MAIL_APP_DISPLAY_NAME, MAIL_SUITE_APP_TILE };

declare global {
  interface Window {
    __SUITE_SHARED_READY__?: boolean;
    __mailSessionVerified?: boolean;
    __mailSuiteAuthStarted?: boolean;
    __mailHideAuthModalPatched?: boolean;
    __mailShowAuthModalPatched?: boolean;
    clearSuiteStoredIdentity?: () => void;
    lockSuiteAppShell?: () => void;
    unlockSuiteAppShell?: () => void;
    suiteAuthModalMount?: (opts: { appName?: string }) => void;
    suiteForbiddenModalMount?: (opts: { appName?: string }) => void;
    suiteLogoutModalsMount?: (opts: { appName?: string }) => void;
    installSuiteAppShell?: (opts: { shellId: string }) => void;
    installSuiteClearCacheListener?: (opts: { preserveKeys?: string[] }) => void;
    showSuiteForbiddenModal?: (opts?: {
      appName?: string;
      actions?: Array<{ label: string; primary?: boolean; onClick?: () => void }>;
    }) => void;
    hideSuiteForbiddenModal?: () => void;
    getSuiteAuthPopupUrl?: (redirect?: string) => string;
    openSuiteAuthPopup?: (redirect?: string) => boolean;
    showSuiteAuthModal?: (message?: unknown, options?: { autoReconnect?: boolean }) => void;
    hideSuiteAuthModal?: () => void;
    showSuiteLogoutConfirmModal?: (opts: { onConfirm?: () => void }) => void;
    stopSuiteHubSessionWatch?: () => void;
    stopSuiteIframeSsoPoll?: () => void;
    startSuiteIframeSsoPoll?: () => void;
    enableSuiteAuthAutoReconnect?: () => void;
    startSuiteHubSessionWatch?: () => void;
  }
}
